import { apiClient, tokenStorage } from './api'
import type {
  RelationshipImportValidationResult,
  RelationshipImportExecuteRequest,
  RelationshipImportExecuteResult,
  UnresolvedReference,
} from '../types/relationship-import'

import { API_BASE_URL } from './api-base-url'

const DEFAULT_FILENAME = 'unresolved_references.csv'
const RELATIONSHIP_TEMPLATE_FILENAME = 'relationship_template.csv'

function buildAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {}
  const token = tokenStorage.getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function parseErrorResponse(response: Response): Promise<Error> {
  let errorBody: { error: string; message: string } | null = null
  try {
    errorBody = (await response.json()) as { error: string; message: string }
  } catch {
    errorBody = null
  }
  return new Error(errorBody?.message ?? response.statusText)
}

function extractFilename(response: Response, fallback: string): string {
  const contentDisposition = response.headers.get('Content-Disposition')
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
    if (filenameMatch?.[1]) {
      return filenameMatch[1]
    }
  }
  return fallback
}

function triggerDownload(blob: Blob, filename: string): void {
  const downloadUrl = globalThis.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  globalThis.URL.revokeObjectURL(downloadUrl)
}

export async function validateRelationshipImport(
  caseId: string,
  file: File
): Promise<RelationshipImportValidationResult> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE_URL}/cases/${caseId}/relationships/import/validate`, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: formData,
  })

  if (!response.ok) {
    throw await parseErrorResponse(response)
  }

  return response.json() as Promise<RelationshipImportValidationResult>
}

export async function executeRelationshipImport(
  caseId: string,
  request: RelationshipImportExecuteRequest
): Promise<RelationshipImportExecuteResult> {
  return apiClient.post<RelationshipImportExecuteResult>(
    `/cases/${caseId}/relationships/import/execute`,
    request
  )
}

export async function downloadRelationshipTemplate(caseId: string): Promise<void> {
  const url = `${API_BASE_URL}/cases/${caseId}/relationships/import/template`

  const response = await fetch(url, {
    method: 'GET',
    headers: buildAuthHeaders(),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response)
  }

  const filename = extractFilename(response, RELATIONSHIP_TEMPLATE_FILENAME)
  const blob = await response.blob()
  triggerDownload(blob, filename)
}

export async function exportUnresolvedReferences(
  caseId: string,
  references: UnresolvedReference[]
): Promise<void> {
  const url = `${API_BASE_URL}/cases/${caseId}/relationships/import/unresolved`
  const authHeaders = buildAuthHeaders()

  const response = await fetch(url, {
    method: 'POST',
    headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders),
    body: JSON.stringify({ references }),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response)
  }

  const filename = extractFilename(response, DEFAULT_FILENAME)
  const blob = await response.blob()
  triggerDownload(blob, filename)
}
